<template>
    <div class="emergencies">
        <div class="container">
            <div class="columns">
                <div class="column is-3">
                    <app-sidebar></app-sidebar>
                    <onboarding></onboarding>
                </div>
                <div class="column is-9">
                    <div v-if="! currentOffice" class="section mt-5 mb-10">
                        <p class="is-aligned-center">{{ $t('no_office_selected') }}</p>
                    </div>
                    <div v-for="office in offices" :key="office.id" class="mt-7" v-if="currentOffice == office.id">
                        <div class="is-clearfix mb-3">
                            <button
                                @click.prevent="refreshEmergencies()"
                                class="button is-primary is-outlined is-pulled-right has-icon">
                                <b-icon icon="refresh"></b-icon>
                                <span>{{ $t('refresh_button_label') }}</span>
                            </button>
                        </div>
                        <div v-if="! doctor.isCertified">
                            <div class="section p-4 mb-10">
                                <p class="has-text-center">{{ $t('doctor_not_certified_warning') }}</p>
                            </div>
                        </div>
                        <div v-else>
                            <div class="section p-4 mb-10" v-if="Object.keys(emergencies).length == 0">
                                <p class="has-text-center">{{ $t('no_emergencies_placeholder') }}</p>
                            </div>
                            <article class="card" v-for="(emergency, key) in emergencies" :key="key" :id="'emergency-' + key">
                                <div class="card-header clickable" @click.prevent="selectEmergency(key, true)">
                                    <div class="card-header-title">
                                        <div class="media">
                                            <figure class="image thumbnail is-128x128 is-inline-block mr-3" v-if="emergency.pictures.length > 0">
                                                <img :src="emergency.pictures[0]" alt="Image Preview" >
                                            </figure>
                                            <div class="media-content">
                                                <h3 class="title is-6">{{ $t('patient_header', {age: emergency.patientAge, locality: emergency.locality}) }}</h3>
                                                <h4 class="subtitle is-7">{{ timeAgo(emergency.dateTimeCreated, 'X') }}</h4>
                                            </div>
                                        </div>
                                    </div>
                                    <a class="card-header-icon">
                                        <b-icon :icon="openedEmergency == key ?
                                            'arrow-drop-down' : 'arrow-drop-up'">
                                        </b-icon>
                                    </a>
                                </div>
                                <div v-show="openedEmergency == key">
                                    <div class="card-content">
                                        <div class="content">
                                            <h5 class="title is-5">{{ $t('patient_symptoms_title') }}</h5>

                                            <div class="columns is-multiline">
                                                <div class="column is-6" v-for="symptom in emergency.symptoms">
                                                    <div class="field">
                                                        <label v-text="symptom.question"></label>
                                                        <p class="is-size-7"><strong v-text="symptom.answer"></strong></p>
                                                    </div>
                                                </div>
                                            </div>

                                            <hr>

                                            <h5 class="title is-5">{{ $t('patient_information_title') }}</h5>
                                            <div class="columns is-multiline">
                                                <div class="column is-4">
                                                    <div class="field">
                                                        <label>{{ $t('patient_age_label') }}</label>
                                                        <p class="is-size-7"><strong>{{ emergency.patientAge ? emergency.patientAge : 'N/A' }}</strong></p>
                                                    </div>
                                                </div>
                                                <div class="column is-4">
                                                    <div class="field">
                                                        <label>{{ $t('patient_locality_label') }}</label>
                                                        <p class="is-size-7"><strong>{{ emergency.locality ? emergency.locality : 'N/A' }}</strong></p>
                                                    </div>
                                                </div>
                                                <div class="column is-4">
                                                    <div class="field">
                                                        <label>{{ $t('patient_zip_code_label') }}</label>
                                                        <p class="is-size-7"><strong>{{ emergency.zipCode ? emergency.zipCode : 'N/A' }}</strong></p>
                                                    </div>
                                                </div>
                                            </div>

                                            <hr>

                                            <h5 class="title is-5 mb-0">{{ $t('pictures_title') }}</h5>

                                            <div class="message is-warning mt-3" v-if="emergency.pictures.length == 0">
                                                <div class="message-body">
                                                    <b-icon icon="alert"></b-icon>
                                                    <span>{{ $t('no_pictures_placeholder') }}</span>
                                                </div>
                                            </div>

                                            <figure class="image thumbnail is-128x128 is-inline-block" v-for="picture in emergency.pictures" @click.prevent="showPicture(picture)">
                                                <img :src="picture" :alt="$t('picture_alt')" >
                                            </figure>

                                            <hr>

                                            <button class="button is-primary has-icon" @click.prevent="acceptEmergency(key)" :disabled="! loadedAvailabilities">
                                                <b-icon icon="check-circle"></b-icon>
                                                <span>{{ $t('accept_emergency_button_label') }}</span>
                                            </button>
                                        </div>
                                    </div>

                                </div>
                            </article>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <accept-emergency-form
            :active="showAcceptModal"
            @close="toggleEmergencyForm(false)"
            @accepted="refresh()"
            :emergency="selectedEmergency"
            :availabilities="availabilities"></accept-emergency-form>
    </div>
</template>

<script>
import helpers from '@/mixins/helpers'
import { store } from '@/store'
import { db } from '@/firebase'
import moment from 'moment'

import emergencyRepository from '@/data/EmergencyRepository'
import timeSlotRepository from '@/data/TimeSlotRepository'

import AppSidebar from '@/components/Doctor/AppSidebar.vue'
import AcceptEmergencyForm from '@/components/Doctor/AcceptEmergencyForm.vue'
import Onboarding from '@/components/Doctor/Onboarding.vue'

import VueScrollTo from 'vue-scrollto'

export default {
    name: 'emergencies',

    components: { AppSidebar, AcceptEmergencyForm, Onboarding },

    mixins: [helpers],

    data () {
        return {
            emergencies: {},
            showAcceptModal: false,
            openedEmergency: null,
            selectedEmergency: null,
            availabilities: {},
            loadedAvailabilities: false
        }
    },

    mounted () {
        // Check if we queried a specific office
        if (this.$route.query.officeId && this.$route.query.officeId != this.currentOffice) {
            if (typeof this.offices[this.$route.query.officeId] !== 'undefined') {
                this.setCurrentOffice(this.$route.query.officeId)
            } else {
                this.$router.push({ name: 'doctor.emergencies' })
            }
        }

        if (this.doctor.isCertified) {
            this.refresh()
        }

        this.$on('emergency-accepted', () => {
            this.refresh()
        })
    },

    methods: {
        refresh () {
            this.refreshEmergencies()
            this.refreshOfficeAvailabilities()
        },

        refreshEmergencies () {
            this.setLoading(true)

            emergencyRepository.allAvailable(this.doctor.type, (emergencies) => {
                this.emergencies = emergencies

                this.openedEmergency = null

                for (let emergency in emergencies) {
                    this.selectEmergency(emergency)
                    break
                }

                this.setLoading(false)
            }, () => {
                this.alertError(this.$t('emergencies_could_not_be_loaded'))

                this.setLoading(false)
            })
        },

        refreshOfficeAvailabilities () {
            if (! this.doctor) {
                return false;
            }

            this.loadedAvailabilities = false
            this.setLoading(true)

            timeSlotRepository.allFutureAvailable(this.doctor.id, this.currentOffice, (timeSlots) => {
                this.availabilities = timeSlots

                this.setLoading(false)
                this.loadedAvailabilities = true
            }, (error) => {
                this.alertError(error.message)

                this.setLoading(false)
            })
        },

        selectEmergency (key, scroll) {
            this.openedEmergency = this.openedEmergency == key ? null : key

            this.$nextTick(() => {
                if (scroll === true) {
                    let div = document.getElementById('emergency-' + key)
                    VueScrollTo.scrollTo(div, 500)
                }
            })
        },

        acceptEmergency (key) {
            this.openedEmergency = key
            this.selectedEmergency = this.emergencies[key]
            this.toggleEmergencyForm(true)
        },

        toggleEmergencyForm (open) {
            this.showAcceptModal = open
        },

        showPicture (picture) {
            this.$modal.open(
                `<p class="image">
                    <img src="${picture}" alt="Image" />
                </p>`
            )
        }
    },

    watch: {
        currentOffice (newCurrentOffice) {
            this.refresh()
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/Doctor/Emergencies.vue